import { valuesAtMessagePath } from "@/shared/components/visualization/messagePath/valuesAtMessagePath";
import type { FileService } from "@/shared/domain/files";
import type { TopicService } from "@/shared/domain/topics";
import { ReaderRegistry } from "@/shared/reader";

import type { ReadFirstMessage } from "./resolveDefaultBindings";

/**
 * A {@link ReadFirstMessage} backed by the real topic readers.
 *
 * The one piece of `resolveDefaultBindings` that touches the network. It is
 * separate so that the resolution rules stay a pure function over fake topic
 * trees, and so the thing that fetches megabytes of MCAP can be swapped for a
 * stub without pretending to resolve anything.
 *
 * No new worker command, and no data server: `ReaderRegistry` works from the
 * main thread — the data-server worker is itself only a thin wrapper over it —
 * and one message of one topic is a far smaller read than the panel's own. The
 * cost is real all the same: this makes model import asynchronous *on data
 * availability*, which it has never been before, so every caller needs an abort
 * path and a story for "the read failed" that still imports the model.
 *
 * A fresh registry per call. Reader instances hold decoded chunks, and this
 * reads exactly one message per topic at import time; keeping them alive
 * afterwards would hold that memory for the life of the panel.
 *
 * @param services the file and topic services the readers fetch through
 * @returns a reader that decodes a topic's first message and returns the values
 *   at one path in it, or `undefined` if the topic carries no messages
 */
export function makeFirstMessageReader(services: {
  files: FileService;
  topics: TopicService;
}): ReadFirstMessage {
  return async (node, signal) => {
    const registry = new ReaderRegistry({
      fileService: services.files,
      topicService: services.topics,
    });
    const topicId = node.topic.id;
    const messagePathId = node.data.message_path_id;
    try {
      const reader = await registry.forTopic({ topicId, signal });
      for await (const message of reader.iterMessages({
        messagePathsInclude: [messagePathId],
        signal,
      })) {
        // The first message decides the whole recording's element ordering. See
        // `resolveDefaultBindings` for why that is sound in practice and not
        // guaranteed by any message definition.
        return valuesAtMessagePath(message.data, node.toParts());
      }
      return undefined;
    } finally {
      registry.dispose(topicId);
    }
  };
}
